import * as THREE from 'three/webgpu';
import {
    AssemblyTransformToolMode,
    MoveComponentInputMode,
} from '../../assembly/move-component/MoveComponentTypes';
import type { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { AssemblyPartDragStrategy } from '../drag';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import type { MoveComponentController } from '../../assembly/move-component/MoveComponentController';
import { AssemblyEntitySelectionHandler } from './AssemblyEntitySelectionHandler';
import type { AssemblyEntitySelectionHandlerOptions } from './AssemblyEntitySelectionHandler';

/**
 * 移动零部件命令专属选择处理器。
 *
 * 设计说明：
 * 1. 当前类只承接移动零部件命令里的沿实体参考选择与受约束拖拽；
 * 2. 通过继承 `AssemblyEntitySelectionHandler` 复用 imported-model 选择、代理高亮与整零件拖拽解析；
 * 3. 装配体默认空闲态与移动命令态选择逻辑彻底分离，避免继续把命令语义塞回装配体默认 handler。
 */
export class MoveComponentEntitySelectionHandler extends AssemblyEntitySelectionHandler {
    /**
     * 创建移动零部件命令专属选择处理器。
     *
     * @param entitySelectionManager 实体选择管理器。
     * @param importedModelRaycastResolver 导入模型射线解析器。
     * @param options 处理器行为选项。
     */
    public constructor(
        entitySelectionManager: EntitySelectionManager,
        importedModelRaycastResolver: ImportedModelRaycastResolver,
        options: AssemblyEntitySelectionHandlerOptions,
    ) {
        super(entitySelectionManager, importedModelRaycastResolver, options);
    }

    /**
     * 处理鼠标按下。
     *
     * 设计说明：
     * 1. 沿实体模式必须先选参考实体，再选零部件拖拽；
     * 2. 当零部件拖拽开始后，参考实体必须继续保留在 selected 集合中；
     * 3. 除上述命令专属语义外，其余 imported-model 行为继续复用父类实现。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        if (this.handleMoveComponentCollisionCheckComponentSelection(payload)) {
            return;
        }
        if (this.handleAlongEntityReferenceSelection(payload)) {
            return;
        }
        if (payload.button === 2) {
            super.onMouseDown(payload);
            return;
        }
        const interactionContext = this.resolveAssemblyPartInteractionContext(payload);
        if (!interactionContext) {
            if (this.isAlongEntityComponentSelectionStage()) {
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }
            super.onMouseDown(payload);
            return;
        }

        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(false);

        if (this.entitySelectionManager.getPrimarySelectedObject()?.uuid !== interactionContext.selectionPick.object.uuid) {
            this.entitySelectionManager.updateSelectionState(
                this.resolveNextSelectionState(
                    this.toIntersections(this.resolveMoveComponentDragSelectedObjects(interactionContext.selectionPick.object)),
                    false,
                ),
            );
        }

        this.clearHoverState();
        const operationMode = this.resolveAuthorizedAssemblyPartDragOperationModeForTarget({
            targetObject: interactionContext.transformRootObject,
            requestedOperationMode: this.resolveAssemblyPartDragOperationMode(payload.button),
            button: payload.button,
        });
        if (!operationMode) {
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }
        this.startDrag(new AssemblyPartDragStrategy(
            this.entitySelectionManager.renderer,
            interactionContext.transformRootObject,
            this.entitySelectionManager.renderer.moveComponentController?.getAlongEntityReference?.() ?? null,
            operationMode,
        ), payload);
        this.entitySelectionManager.setSuppressNextClick(true);
        payload.nativeEvent.preventDefault();
    }

    /**
     * 处理沿实体模式下的参考实体选择。
     *
     * @param payload 原始事件。
     * @returns 是否已由当前分支处理。
     */
    private handleAlongEntityReferenceSelection(payload: RawPointerEventPayload): boolean {
        const controller = this.entitySelectionManager.renderer.moveComponentController;
        if (!controller || !this.isAlongEntityReferenceSelectionStage()) {
            return false;
        }

        if (payload.button !== 0 || payload.ctrlKey) {
            return false;
        }

        const picked = this.resolveAlongEntityReferencePick(payload.clientX, payload.clientY, controller);
        if (!picked?.object || !controller.canUseAsAlongEntityReference(picked.object)) {
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return true;
        }

        const referenceStored = controller.setAlongEntityReference(picked.object);
        if (!referenceStored) {
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return true;
        }

        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(true);
        this.entitySelectionManager.updateSelectionState(
            this.resolveNextSelectionState(this.toIntersections([picked.object]), false),
        );
        payload.nativeEvent.preventDefault();
        return true;
    }

    //------ 沿实体参考代理命中优先级 开始------
    /**
     * 解析沿实体参考命中结果。
     *
     * 设计说明：
        * 1. imported 点线面代理只会在 hover 二阶段命中后按需创建并保留；
        * 2. 因此沿实体参考选择必须优先复用当前 hover 保留的代理对象，不能重新退回 raw mesh 命中；
        * 3. 若当前没有合法 hover 参考，再回退到普通点击拾取，保持基准/普通特征对象行为不变。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param controller 移动零部件控制器。
     * @returns 当前应作为沿实体参考的命中结果；未命中时返回 `null`。
     */
    private resolveAlongEntityReferencePick(
        clientX: number,
        clientY: number,
        controller: MoveComponentController,
    ): THREE.Intersection<THREE.Object3D> | null {
        const hoveredPick = this.entitySelectionManager.getHoveredPick();
        if (hoveredPick?.object && controller.canUseAsAlongEntityReference(hoveredPick.object)) {
            return hoveredPick;
        }

        return this.pickEntityAt(clientX, clientY, this.collectClickSelectionTargets());
    }
    //------ 沿实体参考代理命中优先级 结束------

    /**
     * 判断当前是否处于沿实体“等待参考实体”阶段。
     *
     * @returns 是否处于等待参考阶段。
     */
    private isAlongEntityReferenceSelectionStage(): boolean {
        const controller = this.entitySelectionManager.renderer.moveComponentController;
        return controller?.isActive?.() === true
            && controller.getToolMode?.() === AssemblyTransformToolMode.Move
            && controller.getMoveInputMode?.() === MoveComponentInputMode.AlongEntity
            && controller.hasAlongEntityReference?.() !== true;
    }

    /**
     * 判断当前是否处于沿实体“等待零部件拖拽”阶段。
     *
     * @returns 是否处于等待零部件阶段。
     */
    private isAlongEntityComponentSelectionStage(): boolean {
        const controller = this.entitySelectionManager.renderer.moveComponentController;
        return controller?.isActive?.() === true
            && controller.getToolMode?.() === AssemblyTransformToolMode.Move
            && controller.getMoveInputMode?.() === MoveComponentInputMode.AlongEntity
            && controller.hasAlongEntityReference?.() === true;
    }

    /**
     * 解析移动零部件拖拽时应保留的选中对象集合。
     *
     * @param primaryObject 当前主选对象。
     * @returns 本次应写入选择状态的对象集合。
     */
    private resolveMoveComponentDragSelectedObjects(primaryObject: THREE.Object3D): THREE.Object3D[] {
        const selectedObjects: THREE.Object3D[] = [primaryObject];
        const referenceObject = this.entitySelectionManager.renderer.moveComponentController?.getAlongEntityReferenceObject?.() ?? null;
        if (!referenceObject || referenceObject.uuid === primaryObject.uuid) {
            return selectedObjects;
        }

        selectedObjects.push(referenceObject);
        return selectedObjects;
    }
}
